import { SCENARIO_KEYS, SCENARIO_LABELS } from '../model/scenarios';
import { cumulativeSignupToPaid } from '../model/funnel';

export default function ScenariosPanel({
  scenario,
  activeKey,
  onSelectScenario,
  onResetPack,
  onChangeCompareMode,
  onChangeMonteCarlo,
}) {
  const packs = scenario.packs || {};

  return (
    <div className="forecast-scenarios">
      <section className="chart-card economics-panel">
        <h2>Scenario packs</h2>
        <p className="chart-hint economics-muted">
          Bear / base / bull are separate assumption packs. Switch active scenario or compare all
          three in Results.
        </p>

        <div className="economics-preset-row">
          {SCENARIO_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={activeKey === key ? 'primary-button' : 'secondary-button'}
              onClick={() => onSelectScenario(key)}
            >
              {SCENARIO_LABELS[key]}
            </button>
          ))}
        </div>

        <label className="economics-field">
          <span>Results compare mode</span>
          <select
            value={scenario.compareMode || 'active'}
            onChange={(e) => onChangeCompareMode(e.target.value)}
          >
            <option value="active">Active scenario only</option>
            <option value="all">Compare bear / base / bull</option>
          </select>
        </label>

        <button
          type="button"
          className="secondary-button"
          onClick={() => {
            if (window.confirm(`Reset ${SCENARIO_LABELS[activeKey]} pack to defaults?`)) {
              onResetPack(activeKey);
            }
          }}
        >
          Reset {SCENARIO_LABELS[activeKey]} defaults
        </button>
      </section>

      <section className="chart-card">
        <h2>Pack summary</h2>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Growth option</th>
                <th>Opening logos</th>
                <th>Signup→paid</th>
                <th>M1 retention</th>
                <th>Expansion</th>
              </tr>
            </thead>
            <tbody>
              {SCENARIO_KEYS.map((key) => {
                const pack = packs[key];
                const ret = pack?.cohorts?.retention || [];
                return (
                  <tr key={key} className={activeKey === key ? 'forecast-row-active' : ''}>
                    <td>{SCENARIO_LABELS[key]}</td>
                    <td>{pack?.growth?.preset || '—'}</td>
                    <td>{pack?.assumptions?.openingLogos}</td>
                    <td>{(cumulativeSignupToPaid(pack?.funnel || {}) * 100).toFixed(1)}%</td>
                    <td>{ret[1] != null ? `${(ret[1] * 100).toFixed(0)}%` : '—'}</td>
                    <td>{pack?.assumptions?.expansionPctMo}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="chart-card economics-panel">
        <h2>Monte Carlo</h2>
        <p className="chart-hint">
          Each run samples a <strong>growth regime</strong> (fall flat / traction / explode by
          default 25/50/25), then monthly growth noise + conversion/churn/CAC. Your Growth-tab
          phase table applies when the sampled regime matches the active preset (custom edits →
          traction weight). Other regimes use stock Fall flat / Explode. Toggle Mix off to use
          your phases on every run.
        </p>
        <div className="economics-form-grid economics-form-grid-3">
          <label className="economics-field forecast-toggle">
            <input
              type="checkbox"
              checked={scenario.monteCarlo?.enabled !== false}
              onChange={(e) => onChangeMonteCarlo({ enabled: e.target.checked })}
            />
            <span>Enable Monte Carlo</span>
          </label>
          <label className="economics-field forecast-toggle">
            <input
              type="checkbox"
              checked={scenario.monteCarlo?.mixGrowthRegimes !== false}
              onChange={(e) => onChangeMonteCarlo({ mixGrowthRegimes: e.target.checked })}
              disabled={scenario.monteCarlo?.enabled === false}
            />
            <span>Mix growth regimes</span>
          </label>
          <label className="economics-field">
            <span>Runs</span>
            <input
              type="number"
              min="50"
              max="5000"
              value={scenario.monteCarlo?.runs ?? 150}
              onChange={(e) =>
                onChangeMonteCarlo({
                  runs: Math.min(5000, Math.max(50, parseInt(e.target.value, 10) || 150)),
                })
              }
              disabled={scenario.monteCarlo?.enabled === false}
            />
          </label>
          <label className="economics-field">
            <span>Seed</span>
            <input
              type="number"
              value={scenario.monteCarlo?.seed ?? 20260812}
              onChange={(e) =>
                onChangeMonteCarlo({
                  seed: Number.isFinite(parseInt(e.target.value, 10))
                    ? parseInt(e.target.value, 10)
                    : 20260812,
                })
              }
              disabled={scenario.monteCarlo?.enabled === false}
            />
          </label>
          <label className="economics-field">
            <span>Conversion vol %</span>
            <input
              type="number"
              min="0"
              max="100"
              value={scenario.monteCarlo?.conversionVolPct ?? 20}
              onChange={(e) =>
                onChangeMonteCarlo({
                  conversionVolPct: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)),
                })
              }
              disabled={scenario.monteCarlo?.enabled === false}
            />
          </label>
          <label className="economics-field">
            <span>Churn vol %</span>
            <input
              type="number"
              min="0"
              max="100"
              value={scenario.monteCarlo?.churnVolPct ?? 15}
              onChange={(e) =>
                onChangeMonteCarlo({
                  churnVolPct: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)),
                })
              }
              disabled={scenario.monteCarlo?.enabled === false}
            />
          </label>
          <label className="economics-field">
            <span>CAC vol %</span>
            <input
              type="number"
              min="0"
              max="100"
              value={scenario.monteCarlo?.cacVolPct ?? 25}
              onChange={(e) =>
                onChangeMonteCarlo({
                  cacVolPct: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)),
                })
              }
              disabled={scenario.monteCarlo?.enabled === false}
            />
          </label>
        </div>
        <h3 className="economics-subheading">Growth regime mix</h3>
        <p className="chart-hint economics-muted">
          These are sampling weights, not extra growth. They are normalized to 100% before each
          projection; custom Growth phases occupy the Traction bucket.
        </p>
        <div className="economics-form-grid economics-form-grid-3">
          {[
            ['fall_flat', 'Fall-flat share'],
            ['traction', 'Traction share'],
            ['explode', 'Explode share'],
          ].map(([key, label]) => (
            <label key={key} className="economics-field">
              <span>{label}</span>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={scenario.monteCarlo?.growthRegimeMix?.[key] ?? 0}
                onChange={(e) =>
                  onChangeMonteCarlo({
                    growthRegimeMix: {
                      ...scenario.monteCarlo?.growthRegimeMix,
                      [key]: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)),
                    },
                  })
                }
                disabled={
                  scenario.monteCarlo?.enabled === false ||
                  scenario.monteCarlo?.mixGrowthRegimes === false
                }
              />
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}
